export const PAT_ELIGIBILITY_MAX_RETRY_ATTEMPTS = 3

// Selecting "Lane Default" carries this sentinel saleTypeRef — the default is re-derived from the
// lane assignment downstream (AS400), so no explicit override reference is ever sent for it.
// Exported so both SaleTypeEligibilityStatus.js (dropdown) and AssignInventoryDetail.js
// (getVehicleSaleType's restored-snapshot fallback) reference the same sentinel, not a duplicated literal.
export const LANE_DEFAULT_SALE_TYPE = { saleType: 'Lane Default', saleTypeRef: 'LANE_DEFAULT' }

// The label shown (and persisted on Save As Draft) when a vehicle is PAT-eligible but resolved with
// nothing eligible to offer (FR-006i). Renamed from 'SALE TYPE' (2026-09-10) to avoid confusion with
// the unrelated legacy Sale Type vehicle detail field. Exported so SaleTypeEligibilityStatus.js's
// render and this file's own derivePatDraftSnapshot always agree on the exact string, not a
// duplicated literal.
export const FEE_SALE_TYPE_LABEL = 'FEE SALE TYPE'

const initialState = {
    entriesByKey: {},
    // Lane-level PAT pre-check result from "GO" (setLaneOverrideEligible/resetLaneOverrideEligible in
    // PatEligibilityAction.js) - defaults to true ("assume eligible") until the pre-check for the
    // current search resolves, or when it hasn't run at all (e.g. the PAT toggle is off for this
    // auction, so no pre-check is ever fired - see AssignInventorySearchBar.js).
    isLaneOverrideEligible: true
}

export function getPatEligibilityKey(vin, assignmentInstanceId) {
    return `${vin}::${assignmentInstanceId}`
}

// FR-006/FR-006a: whether a vehicle's resolved eligibility actually offers an override to choose
// from. An empty overrideSaleTypes array ([]) counts the same as null — no real list to offer.
export function entryHasOverrideToOffer(entry) {
    const hasOverrideList = Array.isArray(entry?.overrideSaleTypes) && entry.overrideSaleTypes.length > 0
    return !!entry?.overrideEligible && !!(entry?.recommendedOverride || hasOverrideList)
}

// US2165220: only an actual PAT recommendation forces an explicit decision before Send to AS400 —
// "Lane Default" already applies by default (getVehicleSaleType in AssignInventoryDetail.js) whenever
// PAT has nothing specific to recommend, whether or not overrideSaleTypes still offers optional manual
// picks instead.
export function entryRequiresSaleTypeSelection(entry) {
    return entry?.status === 'resolved' && !!entry?.overrideEligible && !!entry?.recommendedOverride && !entry?.selectedSaleType
}

// A failed eligibility check must be retried successfully before the vehicle can be persisted —
// otherwise Send to AS400 would silently fall back to sending '' (Lane Default) for a vehicle whose
// real sale type is still unknown, since getVehicleSaleType() only knows how to read a resolved
// entry. Used to gate Send to AS400 alongside entryRequiresSaleTypeSelection above.
export function entryRequiresRetryBeforeSend(entry) {
    return entry?.status === 'error'
}

// FR-006i: a resolved check that came back with nothing eligible to offer at all.
export function entryHasNoOverrideToOffer(entry) {
    return entry?.status === 'resolved' && !entryHasOverrideToOffer(entry)
}

// US2054837 (revised): the 2-field draft snapshot persisted on Save As Draft - patSelectedSaleType
// (the full { saleType, saleTypeRef } object currently applied, or '' when there's nothing to show at
// all) and patLabel (one of ASSIGNED/AUTO-UPDATED/REVERTED/FEE SALE TYPE, or '' to
// match). Deliberately mirrors (does not share code with) the label derivation inline in
// SaleTypeEligibilityStatus.js's render — kept separate so a save-time-only change here can't regress
// the already-tested live dropdown render; keep both in sync if either changes.
// patSelectedSaleType is '' only when the vehicle was never resolved at all (loading/error/no entry —
// genuinely nothing to show). A resolved check that offers no override to pick from at all (FR-006i,
// US2003003) still has something real on screen — "FEE SALE TYPE" + "Lane Default", with Edit/Cancel —
// so it's persisted as the LANE_DEFAULT_SALE_TYPE sentinel + 'FEE SALE TYPE' rather than collapsed into
// '' (bug found in manual testing: the label vanished on reload though the row always shows something).
// 'FEE SALE TYPE' renamed from 'SALE TYPE' (2026-09-10) to avoid confusion with the unrelated legacy
// Sale Type vehicle detail field - drafts saved before this change keep their old persisted
// 'SALE TYPE' string as-is until saved again (not backfilled).
// When the vehicle IS resolved with an override to offer but nothing has been picked yet and there's
// no recommendedOverride (US2165220: FEE SALE TYPE, not NEEDS ASSIGNMENT - nothing is actually
// required), patSelectedSaleType is also the LANE_DEFAULT_SALE_TYPE sentinel object, not '' —
// patLabel is what distinguishes all three of these Lane-Default-sentinel cases from each other. Either
// way, the existing "falsy/LANE_DEFAULT saleTypeRef → Lane Default display, send '' to AS400"
// conventions apply uniformly.
export function derivePatDraftSnapshot(entry) {
    if (entry?.status !== 'resolved') {
        return { patSelectedSaleType: '', patLabel: '' }
    }
    if (!entryHasOverrideToOffer(entry)) {
        return { patSelectedSaleType: LANE_DEFAULT_SALE_TYPE, patLabel: FEE_SALE_TYPE_LABEL }
    }
    const currentRef = entry.selectedSaleType?.saleTypeRef ?? null
    const firstRenderedRef = entry.firstRenderedValue?.saleTypeRef ?? null
    const showUndo = entry.isReverted || currentRef !== firstRenderedRef
    const patLabel = entry.isReverted
        ? 'REVERTED'
        : showUndo
            ? 'ASSIGNED'
            : entry.recommendedOverride ? 'AUTO-UPDATED' : FEE_SALE_TYPE_LABEL
    return {
        patSelectedSaleType: entry.selectedSaleType ?? LANE_DEFAULT_SALE_TYPE,
        patLabel
    }
}

export const patEligibilityReducer = (state = initialState, action) => {
    switch (action.type) {
        case 'PAT_ELIGIBILITY_REQUEST': {
            const key = getPatEligibilityKey(action.vin, action.assignmentInstanceId)
            return {
                ...state,
                entriesByKey: {
                    ...state.entriesByKey,
                    [key]: {
                        vin: action.vin,
                        assignmentInstanceId: action.assignmentInstanceId,
                        status: 'loading',
                        attemptCount: 0,
                        defaultSaleType: null,
                        overrideEligible: false,
                        recommendedOverride: null,
                        overrideSaleTypes: null,
                        selectedSaleType: null,
                        hasManualSelection: false,
                        firstRenderedValue: undefined,
                        isReverted: false,
                        errorMessage: null
                    }
                }
            }
        }
        case 'PAT_ELIGIBILITY_RETRY': {
            const key = getPatEligibilityKey(action.vin, action.assignmentInstanceId)
            const entry = state.entriesByKey[key]
            if (!entry) {
                return state
            }
            return {
                ...state,
                entriesByKey: {
                    ...state.entriesByKey,
                    [key]: {
                        ...entry,
                        status: 'loading',
                        attemptCount: entry.attemptCount + 1
                    }
                }
            }
        }
        case 'PAT_ELIGIBILITY_SUCCESS': {
            const key = getPatEligibilityKey(action.vin, action.assignmentInstanceId)
            const entry = state.entriesByKey[key]
            if (!entry) {
                return state
            }
            const result = action.result || {}
            const overrideSaleTypes = result.overrideSaleTypes ?? null
            const defaultSaleType = result.defaultSaleType
                ? { saleType: result.defaultSaleType.saleType, saleTypeRef: result.defaultSaleType.saleTypeRef }
                : null
            const recommendedOverride = result.recommendedOverride
                ? { saleType: result.recommendedOverride.saleType, saleTypeRef: result.recommendedOverride.saleTypeRef }
                : null
            const selectedSaleType = entry.hasManualSelection ? entry.selectedSaleType : recommendedOverride
            // Captured once per assignment (key), on the first successful response — the baseline
            // Undo restores to (spec.md "first-rendered value"). A lane change produces a fresh
            // assignmentInstanceId/key, so this naturally re-captures for the new assignment context.
            const firstRenderedValue = entry.firstRenderedValue === undefined ? selectedSaleType : entry.firstRenderedValue

            return {
                ...state,
                entriesByKey: {
                    ...state.entriesByKey,
                    [key]: {
                        ...entry,
                        status: 'resolved',
                        defaultSaleType,
                        overrideEligible: !!result.overrideEligible,
                        recommendedOverride,
                        overrideSaleTypes,
                        selectedSaleType,
                        firstRenderedValue
                    }
                }
            }
        }
        case 'PAT_ELIGIBILITY_ERROR': {
            const key = getPatEligibilityKey(action.vin, action.assignmentInstanceId)
            const entry = state.entriesByKey[key]
            if (!entry) {
                return state
            }
            return {
                ...state,
                entriesByKey: {
                    ...state.entriesByKey,
                    [key]: {
                        ...entry,
                        status: 'error',
                        errorMessage: action.errorMessage || null
                    }
                }
            }
        }
        case 'PAT_ELIGIBILITY_SELECT_SALE_TYPE': {
            const key = getPatEligibilityKey(action.vin, action.assignmentInstanceId)
            const entry = state.entriesByKey[key]
            if (!entry) {
                return state
            }
            return {
                ...state,
                entriesByKey: {
                    ...state.entriesByKey,
                    [key]: {
                        ...entry,
                        selectedSaleType: action.saleType,
                        hasManualSelection: true,
                        isReverted: false
                    }
                }
            }
        }
        case 'PAT_ELIGIBILITY_REVERT': {
            const key = getPatEligibilityKey(action.vin, action.assignmentInstanceId)
            const entry = state.entriesByKey[key]
            if (!entry) {
                return state
            }
            return {
                ...state,
                entriesByKey: {
                    ...state.entriesByKey,
                    [key]: {
                        ...entry,
                        selectedSaleType: LANE_DEFAULT_SALE_TYPE,
                        hasManualSelection: true,
                        isReverted: true
                    }
                }
            }
        }
        case 'PAT_ELIGIBILITY_UNDO': {
            const key = getPatEligibilityKey(action.vin, action.assignmentInstanceId)
            const entry = state.entriesByKey[key]
            if (!entry) {
                return state
            }
            return {
                ...state,
                entriesByKey: {
                    ...state.entriesByKey,
                    [key]: {
                        ...entry,
                        selectedSaleType: entry.firstRenderedValue ?? null,
                        hasManualSelection: false,
                        isReverted: false
                    }
                }
            }
        }
        case 'PAT_ELIGIBILITY_DISCARD': {
            const key = getPatEligibilityKey(action.vin, action.assignmentInstanceId)
            if (!state.entriesByKey[key]) {
                return state
            }
            const entriesByKey = { ...state.entriesByKey }
            delete entriesByKey[key]
            return { ...state, entriesByKey }
        }
        case 'PAT_LANE_OVERRIDE_ELIGIBLE_SET': {
            return { ...state, isLaneOverrideEligible: !!action.overrideEligible }
        }
        case 'PAT_LANE_OVERRIDE_ELIGIBLE_RESET': {
            return { ...state, isLaneOverrideEligible: initialState.isLaneOverrideEligible }
        }
        default:
            return state
    }
}
